import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export function useNotificationUnreadCount() {
	const company = useCompany();
	const queryClient = useQueryClient();
	const previousCount = useRef<number | null>(null);

	const query = useQuery({
		queryKey: QueryKeys.Notifications.UnreadCount(company.id),
		queryFn: async () => (await Api.GetNotificationUnreadCount()).data,
		refetchInterval: 30_000,
	});

	useEffect(() => {
		const count = query.data?.count;
		if (typeof count !== "number") {
			return;
		}
		if (previousCount.current !== null && count > previousCount.current) {
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Posts.All(company.id),
			});
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.Conversations(company.id),
			});
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.ConversationPrefix(company.id),
			});
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Inbox.UnreadCount(company.id),
			});
		}
		previousCount.current = count;
	}, [query.data?.count, company.id, queryClient]);

	return query;
}

export function useNotifications(enabled: boolean) {
	const company = useCompany();

	return useQuery({
		queryKey: QueryKeys.Notifications.List(company.id),
		queryFn: async () => (await Api.GetNotifications()).data,
		enabled,
	});
}

export function useNotificationActions() {
	const company = useCompany();
	const queryClient = useQueryClient();

	const invalidate = async () => {
		await queryClient.invalidateQueries({
			queryKey: QueryKeys.Notifications.List(company.id),
		});
		await queryClient.invalidateQueries({
			queryKey: QueryKeys.Notifications.UnreadCount(company.id),
		});
	};

	const markRead = useMutation({
		mutationFn: async (notificationId: number) => {
			await Api.MarkNotificationRead(notificationId);
			await invalidate();
		},
	});

	const markAllRead = useMutation({
		mutationFn: async () => {
			await Api.MarkAllNotificationsRead();
			await invalidate();
		},
	});

	return { markRead, markAllRead };
}
